import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { http } from './base';
import { IpTrademark } from '../swagger/IpTrademark';
import { Brand } from '../swagger/Brand';
import {
  BrandBrandProtocolAddBrandRequest,
  BrandBrandProtocolDeleteBrandRequest,
  BrandBrandProtocolGetIpTmByBrandRequest,
  BrandBrandProtocolGetIpTmByBrandResponse,
  BrandBrandProtocolUpdateBrandRequest,
  FolioCommonDtoBrandDto,
  IptrademarkIpStatusProtocolGetNotificationListRequest,
  IptrademarkIpTrademarkProtocolAddCustomColumnTitleRequest,
  IptrademarkIpTrademarkProtocolAddCustomColumnTitleResponse,
  IptrademarkIpTrademarkProtocolAddCustomColumnValueRequest,
  IptrademarkIpTrademarkProtocolAddIpTmWithNumberRequest,
  IptrademarkIpTrademarkProtocolAddIpTrademarkWithRegiCertificateRequest,
  IptrademarkIpTrademarkProtocolAddTmWithFileRequest,
  IptrademarkIpTrademarkProtocolAddTmWithFileResponse,
  IptrademarkIpTrademarkProtocolDeleteColumnTitleRequest,
  IptrademarkIpTrademarkProtocolDeleteIpTrademarkRequest,
  IptrademarkIpTrademarkProtocolGetColumnTitleListResponse,
  IptrademarkIpTrademarkProtocolGetDownloadUrlRequest,
  IptrademarkIpTrademarkProtocolGetDownloadUrlResponse,
  IptrademarkIpTrademarkProtocolGetIpTmRegistrationListRequest,
  IptrademarkIpTrademarkProtocolGetIpTmRegistrationListResponse,
  IptrademarkIpTrademarkProtocolGetIpTmResponseIpTmName,
  IptrademarkIpTrademarkProtocolGetIpTrademarkDetailsRequest,
  IptrademarkIpTrademarkProtocolGetIpTrademarkDetailsResponse,
  IptrademarkIpTrademarkProtocolGetRegiCertifiPresignedUrlRequest,
  IptrademarkIpTrademarkProtocolGetRegiCertifiPresignedUrlResponse,
  IptrademarkIpTrademarkProtocolUpdateColumnOrderRequest,
  IptrademarkIpTrademarkProtocolUpdateColumnTitleRequest,
  IptrademarkIpTrademarkProtocolUpdateIpTrademarkDetailsRequest,
} from '../swagger/data-contracts';
import { queryClient } from '~/index';

export const QueryKeyTrademarks = 'trademarks';

const trademarksApi = new IpTrademark(http);
const brandApi = new Brand(http);

export const useGetTrademarksList = (
  request: IptrademarkIpTrademarkProtocolGetIpTmRegistrationListRequest,
  enabled: boolean
): UseInfiniteQueryResult<IptrademarkIpTrademarkProtocolGetIpTmRegistrationListResponse, unknown> => {
  return useInfiniteQuery(
    [QueryKeyTrademarks, 'trademarks-list', request],
    async ({ pageParam }) => {
      const { data } = await trademarksApi.listCreate({
        ...request,
        page: {
          pageNum: pageParam || request.page.pageNum,
          pageSize: request.page.pageSize,
        },
      });

      return data;
    },
    {
      enabled,
      getNextPageParam: (lastPage, allPage) => {
        if (!lastPage.data || lastPage.data.length < request.page.pageSize) {
          return;
        }

        return allPage.length + 1;
      },
    }
  );
};

export const useGetTrademarkDetail = (
  request: IptrademarkIpTrademarkProtocolGetIpTrademarkDetailsRequest
): UseQueryResult<IptrademarkIpTrademarkProtocolGetIpTrademarkDetailsResponse> => {
  return useQuery([QueryKeyTrademarks, 'trademark-details', request], async () => {
    const { data } = await trademarksApi.detailsCreate(request);
    return data;
  });
};

export const useDeleteTm = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolDeleteIpTrademarkRequest
> => {
  const queryClient = useQueryClient();

  return useMutation(trademarksApi.deleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useUpdateTm = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolUpdateIpTrademarkDetailsRequest
> => {
  const queryClient = useQueryClient();

  return useMutation(trademarksApi.updateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademark-details']);
    },
  });
};

export const useGetBrandList = (): UseQueryResult<FolioCommonDtoBrandDto[]> => {
  return useQuery([QueryKeyTrademarks, 'brand-list'], async () => {
    const { data } = await brandApi.listList();
    return data.brandList || [];
  });
};

export const useAddBrand = (): UseMutationResult<AxiosResponse<void>, unknown, BrandBrandProtocolAddBrandRequest> => {
  const queryClient = useQueryClient();

  return useMutation(brandApi.postBrand, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'brand-list']);
    },
  });
};

export const useUpdateBrand = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  BrandBrandProtocolUpdateBrandRequest
> => {
  const queryClient = useQueryClient();

  return useMutation(brandApi.updateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'brand-list']);
    },
  });
};

export const useDeleteBrand = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  BrandBrandProtocolDeleteBrandRequest
> => {
  const queryClient = useQueryClient();

  return useMutation(brandApi.deleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'brand-list']);
    },
  });
};

export const useTrademarksPresigned = (): UseMutationResult<
  AxiosResponse<IptrademarkIpTrademarkProtocolGetRegiCertifiPresignedUrlResponse>,
  unknown,
  IptrademarkIpTrademarkProtocolGetRegiCertifiPresignedUrlRequest
> => {
  return useMutation(trademarksApi.regiCertificatePresignedCreate);
};

export const useNewUploadTrademarkFiles = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolAddIpTrademarkWithRegiCertificateRequest
> => {
  return useMutation(trademarksApi.newRegiCertificateCreate);
};

export const useNewSingleNumber = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolAddIpTmWithNumberRequest
> => {
  return useMutation(trademarksApi.newSingleCreate);
};

export const useNewMultiExcel = (): UseMutationResult<
  AxiosResponse<IptrademarkIpTrademarkProtocolAddTmWithFileResponse>,
  unknown,
  IptrademarkIpTrademarkProtocolAddTmWithFileRequest
> => {
  const queryClient = useQueryClient();

  return useMutation(trademarksApi.newFileCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useGetTmList = (): UseQueryResult<IptrademarkIpTrademarkProtocolGetIpTmResponseIpTmName[]> => {
  return useQuery([QueryKeyTrademarks, 'tm-list'], async () => {
    const { data } = await trademarksApi.ipTmListList();
    return data.ipTmNameList || [];
  });
};

export const useGetTmForBrand = (
  request: BrandBrandProtocolGetIpTmByBrandRequest
): UseQueryResult<BrandBrandProtocolGetIpTmByBrandResponse> => {
  return useQuery([QueryKeyTrademarks, 'tm-brand-list', request], async () => {
    const { data } = await brandApi.ipTmListCreate(request);
    return data;
  });
};

export const useGetTmColumn = (): UseQueryResult<IptrademarkIpTrademarkProtocolGetColumnTitleListResponse> => {
  return useQuery([QueryKeyTrademarks, 'trademarks-column'], async () => {
    const { data } = await trademarksApi.columnTitleListList();
    return data;
  });
};

export const useUpdateTmColumn = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolUpdateColumnTitleRequest
> => {
  return useMutation(trademarksApi.columnTitleUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-column']);
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useAddTmColumn = (): UseMutationResult<
  AxiosResponse<IptrademarkIpTrademarkProtocolAddCustomColumnTitleResponse>,
  unknown,
  IptrademarkIpTrademarkProtocolAddCustomColumnTitleRequest
> => {
  return useMutation(trademarksApi.columnTitleAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-column']);
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useDeleteTmColumn = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolDeleteColumnTitleRequest
> => {
  return useMutation(trademarksApi.columnTitleDeleteCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-column']);
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useUpdateOrderTmColumn = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolUpdateColumnOrderRequest
> => {
  return useMutation(trademarksApi.columnTitleOrderUpdateCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-column']);
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useAddCustomColumn = (): UseMutationResult<
  AxiosResponse<void>,
  unknown,
  IptrademarkIpTrademarkProtocolAddCustomColumnValueRequest
> => {
  return useMutation(trademarksApi.columnCustomAddCreate, {
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-column']);
      queryClient.invalidateQueries([QueryKeyTrademarks, 'trademarks-list']);
    },
  });
};

export const useTmPresigned = (): UseMutationResult<
  AxiosResponse<IptrademarkIpTrademarkProtocolGetRegiCertifiPresignedUrlResponse>,
  unknown,
  IptrademarkIpTrademarkProtocolGetRegiCertifiPresignedUrlRequest
> => {
  return useMutation(trademarksApi.regiCertificatePresignedCreate);
};

export const useTmPresignedDownload = (): UseMutationResult<
  AxiosResponse<IptrademarkIpTrademarkProtocolGetDownloadUrlResponse>,
  unknown,
  IptrademarkIpTrademarkProtocolGetDownloadUrlRequest
> => {
  return useMutation(trademarksApi.urlDownloadCreate);
};

export const useGetTmSampleExcel = () => useMutation(trademarksApi.newMultipleSampleList);

export const useGetTmNew = () =>
  useQuery([QueryKeyTrademarks, 'tm-new'], async () => {
    const { data } = await trademarksApi.noticeList();
    return data;
  });

export const useGetTmLang = () =>
  useQuery([QueryKeyTrademarks, 'lang-list'], async () => {
    const { data } = await trademarksApi.languageListList();

    return data.languageList || [];
  });
